// The free daily case's data (spec web/case-opening): read with the team's appearance, opened by
// the server, which draws and saves the prize before it answers. The browser never picks.
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { useOpenDailyCase } from "../../api/generated/endpoints/appearance/appearance";
import { getGetTeamBalanceQueryKey } from "../../api/generated/endpoints/billing/billing";
import type { AppearanceView, CaseResult, Prize, ThemeView } from "../../api/generated/model";
import { appearanceReads } from "../themes/account";
import type { Odds, ReelCard } from "./reel";

/**
 * A team's read with today's result in it, and the theme it unlocked owned. A case this team hides,
 * or one with nothing left to win, stays as it is: the result is the person's, the hiding the team's.
 */
export function withResult(view: AppearanceView, result: CaseResult): AppearanceView {
  const offered = view.case.status === "waiting" || view.case.status === "opened";
  return {
    ...view,
    case: offered ? { ...view.case, status: "opened", result } : view.case,
    themes: view.themes.map((theme) => (theme.id === result.theme ? { ...theme, owned: true } : theme)),
  };
}

/**
 * Opening today's case. Today's case and its unlock are the person's, so every team's read in the
 * cache takes the result at once: the gift icon's dot goes and the theme shows as owned in any team
 * (spec web/case-opening, decision 7). A jackpot refreshes the balance of the team it paid. These are
 * the mutation's own callbacks, which run even if the dialog is gone by then.
 */
export function useOpenCase() {
  const queryClient = useQueryClient();
  return useOpenDailyCase({
    mutation: {
      onSuccess: (result) => {
        queryClient.setQueriesData<AppearanceView>(appearanceReads, (view) =>
          view === undefined ? view : withResult(view, result),
        );
        if (result.prize === "jackpot") {
          void queryClient.invalidateQueries({ queryKey: getGetTeamBalanceQueryKey(result.team_id) });
        }
      },
    },
  });
}

/** A moment after the case's day ends, so the server has turned to the next one. */
export const DAY_TURN_MS = 1000;

/**
 * The case's day ends at `next_at`, the next UTC midnight. Then every team's read is fetched again:
 * at that moment while the page is open, on coming back to the page after it (a sleeping tab's timers
 * wait), and when the gift menu opens after it (spec web/case-opening, decision 6). Returns the menu's
 * check.
 */
export function useCaseDay(nextAt: string | undefined): () => void {
  const queryClient = useQueryClient();
  const ends = nextAt === undefined ? Number.NaN : Date.parse(nextAt);
  const refreshIfOver = () => {
    if (Date.now() >= ends) void queryClient.invalidateQueries(appearanceReads);
  };
  useEffect(() => {
    if (Number.isNaN(ends)) return;
    const refresh = () => void queryClient.invalidateQueries(appearanceReads);
    const timer = window.setTimeout(refresh, Math.max(0, ends - Date.now()) + DAY_TURN_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible" && Date.now() >= ends) refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [ends, queryClient]);
  return refreshIfOver;
}

/** The reel's odds: each prize's share, and the themes it holds (none for the jackpot). */
export function reelOdds(view: AppearanceView): Odds[] {
  return view.case.odds.map(({ prize, basis_points }) => ({
    tier: prize,
    percent: basis_points / 100,
    families: view.themes.filter((theme: ThemeView) => theme.rarity === prize).map((theme) => theme.id),
  }));
}

/** What the jackpot pays, as the odds say it; none while the jackpot is off. */
export const jackpotCredits = (view: AppearanceView): number | null =>
  view.case.odds.find((odds) => odds.prize === "jackpot")?.credits ?? null;

/** The server's prize as the reel's card. */
export function resultCard(result: CaseResult): ReelCard {
  return { id: result.theme ?? "jackpot", tier: result.prize };
}

const LABELS: Record<Prize, string> = {
  common: "Common",
  rare: "Rare",
  epic: "Epic",
  legendary: "Legendary",
  mythic: "Mythic",
  jackpot: "Jackpot",
};

export const prizeLabel = (prize: Prize): string => LABELS[prize];

/** A share in basis points as live percent: 58%, 0.95%, 0.05%. */
export const percent = (basisPoints: number): string =>
  `${(basisPoints / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;
